import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { User } from "lucide-react";

function LogoutIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5 mr-3 text-red-500"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h6a2 2 0 012 2v1"
      />
    </svg>
  );
}

function AdminIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-5 w-5 mr-3 text-green-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.096 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

export default function ProfileDropdown({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  if (!user) return null;

  const avatarLetter = user.username ? user.username[0].toUpperCase() : "?";

  // Helper to get full avatar URL
  const getAvatarSrc = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    // Assuming backend runs on same host/port or env var
    const apiUrl = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000`; 
    
    // Add cache busting param to force refresh if image changed
    const cleanUrl = `${apiUrl.replace(/\/api$/, '')}${url}`;
    return `${cleanUrl}?v=${Date.now()}`; 
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 p-1 pr-3 rounded-full bg-gradient-to-r from-red-700 to-red-600 border border-yellow-500/50 text-white hover:shadow-[0_0_15px_rgba(255,215,0,0.3)] transition-all duration-300 group"
      >
        <div className="h-8 w-8 rounded-full bg-yellow-100/10 border-2 border-yellow-400/70 overflow-hidden flex items-center justify-center font-bold text-yellow-200">
           {/* Always show initials as per request */}
           {avatarLetter}
        </div>
        <span className="hidden md:block text-sm font-semibold tracking-wide text-yellow-50 group-hover:text-white">
          {user.full_name || user.username}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-52 bg-white/95 backdrop-blur-md rounded-xl shadow-2xl z-[70] py-2 border border-green-400/40 animate-fade-in-up">
            <Link
              to="/dashboard/online/profile"
              onClick={() => setIsOpen(false)}
              className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition font-medium"
            >
              <User size={18} className="mr-3 text-blue-500" />
              Hồ sơ cá nhân
            </Link>

          {user.role === "admin" && (
            <Link
              to="/dashboard/online/admin/users"
              onClick={() => setIsOpen(false)}
              className="flex items-center w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 transition font-medium"
            >
              <AdminIcon />
              Admin Dashboard
            </Link>
          )}
          
          <div className="h-px bg-gray-100 my-1 mx-2"></div>
          
          <button
            onClick={handleLogout}
            className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition font-medium"
          >
            <LogoutIcon />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
